import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { SafeUrl } from '@angular/platform-browser';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faFloppyDisk, faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import panzoom, { PanZoom } from 'panzoom';

@Component({
    selector: 'app-image-viewer',
    imports: [FaIconComponent, MatButtonModule, MatIconModule],
    templateUrl: './image-viewer.component.html',
    styleUrl: './image-viewer.component.scss',
})
export class ImageViewerComponent implements OnDestroy, AfterViewInit {
    /**
     * Références des icônes utilisés dans le template.
     */
    public zoomInIcon = faPlus;
    public zoomOutIcon = faMinus;
    public downloadIcon = faFloppyDisk;

    /**
     * Url vers la représentation mémoire du fichier sélectionné.
     */
    @Input()
    public safeFileUrl: SafeUrl = null;

    /**
     * Emis lorsque l'utilisateur demande de télécharger l'image.
     */
    @Output()
    public downloadRequested: EventEmitter<boolean> = new EventEmitter();

    /**
     * Référence à l'élement HTML img.
     */
    @ViewChild('imageView', { static: false })
    private imageView: ElementRef;

    /**
     * Réference au conteneur de l'image.
     */
    @ViewChild('imageViewer', { static: false })
    private imageViewer: ElementRef;

    /**
     * Framework utilisé pour zoomer et se déplacer dans une image.
     */
    private panzoom: PanZoom;

    /**
     * Constructeur.
     */
    constructor() {}

    /**
     * Appelée à la destruction du composant.
     */
    ngOnDestroy() {
        this.resetViewer();
    }

    /**
     * Appelée après l'initialisation de la vue.
     */
    ngAfterViewInit() {
        if (this.imageView) {
            this.panzoom = panzoom(this.imageView.nativeElement);
        }
    }

    /**
     * Appelée lorsque le navigateur a terminé de charger l'image.
     * @param event : Event du navigateur.
     */
    public onImageLoaded(event): void {
        let heightRatio = this.imageViewer.nativeElement.clientHeight / this.imageView.nativeElement.clientHeight;
        let widthRatio = this.imageViewer.nativeElement.clientWidth / this.imageView.nativeElement.clientWidth;
        let smalestRatio = heightRatio > widthRatio ? widthRatio : heightRatio;

        this.panzoom = panzoom(this.imageView.nativeElement, {
            autocenter: true,
            bounds: true,
            initialZoom: smalestRatio,
            smoothScroll: true,
        });
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Zoomer'.
     * @param event : Evenement de la souris.
     */
    public onZoomIn(event): void {
        event.preventDefault();
        this.zoom(2);
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Dezoomer'.
     * @param event : Evenement de la souris.
     */
    public onZoomOut(event): void {
        event.preventDefault();
        this.zoom(0.5);
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Télécharger'.
     */
    public onDownloadRequested(): void {
        this.downloadRequested.next(true);
    }

    /**
     * Zoome l'image.
     * @param zoomBy : Facteur de zoom (Positif --> agrandissement, négatif --> reduction).
     */
    private zoom(zoomBy: number): void {
        let imageViewer = this.imageViewer.nativeElement;
        let cx = imageViewer.clientLeft + imageViewer.clientWidth / 2;
        let cy = imageViewer.clientTop + imageViewer.clientHeight / 2;
        this.panzoom.smoothZoom(cx, cy, zoomBy);
        // Si l'animation n'est pas necessaire, utiliser ceci :
        // this.panzoom.zoomTo(cx, cy, zoomBy);
    }

    /**
     * Réinitialise le visualisateur.
     */
    private resetViewer() {
        if (this.panzoom) {
            this.panzoom.dispose();
        }
    }
}
